import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import { AdminActions } from "src/redux/Admin/actions";
import { formatKstTime } from "src/utils/dates";
import styles from "./styles.module.scss";

/**
 * The four documents a version is made of, in the order they are shown to a
 * counterparty user. All four are required: a version is the unit that is
 * consented to, so it cannot be published in halves.
 */
export const DOCUMENTS = [
    { name: "terms_of_use_kor", label: "Terms of Use (Korean)" },
    { name: "terms_of_use_eng", label: "Terms of Use (English)" },
    { name: "privacy_policy_kor", label: "Privacy Policy (Korean)" },
    { name: "privacy_policy_eng", label: "Privacy Policy (English)" },
];

/**
 * A file picker whose bar does nothing. The native control is a single click
 * target across its whole width, so it is hidden and driven by the button,
 * leaving the bar to show the chosen name and nothing else.
 *
 * It is hidden with a class rather than `display: none`, because a hidden input
 * still has to be reachable by the click the button forwards to it.
 */
/**
 * The published version, said once. Its name already carries the date, so only
 * the time is added.
 */
const describe = (version) => {
    const by = version.publishedBy ? ` by ${version.publishedBy}` : "";
    // Versions published before the effective date was recorded have none.
    const effective = version.effectiveDate ? `, effective ${version.effectiveDate}` : "";
    return `${version.version} · published ${formatKstTime(version.publishedTimestamp)} KST${by}${effective}`;
};

const FilePicker = ({ name, file, onPick, published }) => {
    const input = useRef(null);
    return (
        <div className={styles.filePicker}>
            <input
                ref={input}
                type="file"
                accept=".html,text/html"
                className={styles.hiddenFileInput}
                data-testid={`terms-file-${name}`}
                onChange={(e) => onPick(e.target.files?.[0] || null)}
            />
            <button
                type="button"
                className={styles.filePickerButton}
                onClick={() => input.current?.click()}
                data-testid={`terms-browse-${name}`}
            >
                Browse
            </button>
            <button
                type="button"
                className={styles.filePickerButton}
                disabled={!published}
                title={
                    published
                        ? "Open the published document"
                        : "Nothing published yet"
                }
                onClick={() =>
                    window.open(`/legal/${name}.html`, "_blank", "noopener")
                }
                data-testid={`terms-show-${name}`}
            >
                Show
            </button>
            <span className={styles.filePickerName}>
                {file ? file.name : "No file selected"}
            </span>
        </div>
    );
};

export const readFile = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error(`Could not read ${file.name}`));
        reader.readAsText(file);
    });

/**
 * Publishes the terms and privacy documents.
 *
 * Publishing puts every signed-in user behind the consent gate, so it is a
 * deliberate act with a confirmation rather than a save. The version name is
 * the server's: the publishing date, with a suffix when that date already has
 * one, so two publishes in a day stay distinct.
 */
/**
 * Publishes the terms and privacy documents.
 *
 * Choosing files stages them like every other control on this page: the change
 * shows in the bottom bar and is applied by Confirm Changes, which is what
 * carries the reason the audit entry records. This component only picks.
 */
// The picker speaks Date, the publish speaks YYYY-MM-DD. Same pair the audit
// page uses for its date filters.
const pad = (n) => String(n).padStart(2, "0");
const toYMD = (d) =>
    d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` : "";
const parseYMD = (s) => (s ? new Date(`${s}T00:00:00`) : null);

const TermsPublisher = ({ files, onPick, effectiveDate, onEffectiveDate, reloadKey }) => {
    const dispatch = useDispatch();
    const [versions, setVersions] = useState([]);
    const [error, setError] = useState(null);
    // Read inside `load` without making it depend on the staged value, which
    // would re-run the fetch on every keystroke of the picker.
    const effectiveDateRef = useRef(effectiveDate);
    effectiveDateRef.current = effectiveDate;

    const load = useCallback(async () => {
        try {
            const res = await dispatch(AdminActions.FetchTermsVersions());
            const loaded = res?.versions || [];
            setVersions(loaded);
            // Show the date in force rather than an empty field: a publish
            // usually carries the date forward, and after one the operator
            // should see what was just published rather than a blank prompt.
            // Only when nothing is staged, so it never overwrites a choice.
            const inForce = loaded[0]?.effectiveDate;
            if (inForce && !effectiveDateRef.current) {
                onEffectiveDate(inForce);
            }
        } catch (e) {
            setError(e.message);
        }
    }, [dispatch, onEffectiveDate]);

    // reloadKey changes when a save completes, so a publish is reflected here
    // without the operator reloading the page.
    useEffect(() => {
        load();
    }, [load, reloadKey]);

    const current = versions.length > 0 ? versions[0] : null;
    const missing = DOCUMENTS.filter((d) => !files[d.name]).length;
    const anyChosen = missing < DOCUMENTS.length;

    return (
        <div className={styles.configForm} data-testid="terms-publisher">
            <div className={`${styles.configRow} ${styles.fileRow}`}>
                <span className={styles.configLabel}>Current version</span>
                <span className={styles.termsVersion} data-testid="terms-current-version">
                    {current ? describe(current) : "Unset"}
                </span>
            </div>

            {DOCUMENTS.map((doc) => (
                <div
                    className={`${styles.configRow} ${styles.fileRow}`}
                    key={doc.name}
                >
                    <span className={styles.configLabel}>{doc.label}</span>
                    <FilePicker
                        name={doc.name}
                        file={files[doc.name]}
                        published={!!current}
                        onPick={(file) => onPick(doc.name, file)}
                    />
                </div>
            ))}

            {/* Stated by the operator, never derived: it is not the day of the
                publish, and the documents' own prose is free to be reworded. */}
            <div className={`${styles.configRow} ${styles.fileRow}`}>
                <span className={styles.configLabel}>Effective date</span>
                <div data-testid="terms-effective-date">
                    <DatePicker
                        selected={parseYMD(effectiveDate)}
                        onChange={(d) => onEffectiveDate(toYMD(d))}
                        dateFormat="yyyy-MM-dd"
                        placeholderText="YYYY-MM-DD"
                        isClearable
                        className={styles.dateInput}
                    />
                </div>
                <span className={styles.configHint}>
                    The date that will be displayed on the consent dialog
                </span>
            </div>

            {anyChosen && !effectiveDate && (
                <div className={`${styles.configRow} ${styles.fileRow}`}>
                    <span className={styles.configLabel} />
                    <span className={styles.termsVersion} data-testid="terms-date-needed">
                        An effective date is needed: it is what the consent
                        dialog states, and nothing else knows it.
                    </span>
                </div>
            )}

            {missing > 0 && missing < DOCUMENTS.length && (
                <div className={`${styles.configRow} ${styles.fileRow}`}>
                    <span className={styles.configLabel} />
                    <span className={styles.termsVersion}>
                        {missing} more document{missing > 1 ? "s" : ""} needed: a
                        version is published as a set of all four.
                    </span>
                </div>
            )}

            {error && <div className={styles.inlineError}>{error}</div>}

            {versions.length > 1 && (
                <div className={`${styles.configRow} ${styles.fileRow}`}>
                    <span className={styles.configLabel}>Earlier versions</span>
                    <span className={styles.configHint}>
                        {versions.slice(1).map((v) => v.version).join(", ")}
                    </span>
                </div>
            )}
        </div>
    );
};

export default TermsPublisher;
